'use client'
import { AnimatePresence, motion } from 'motion/react'
import Link from 'next/link'
/* sanity */
import { groq } from 'next-sanity'
import React, { useEffect } from 'react'

import { client } from '~/app/lib/sanity.client'
import { FooterType, getFooter } from '~/app/lib/sanity.queries'

import { AdvancedButton } from '../atoms/AdvancedButton'
import Button from '../atoms/Button'
import Icon from '../atoms/Icons'
import { ButtonQuery } from '~/app/queries/atoms/ButtonQuery'
import GridDebugger from '../GridDebugger'
import { ButtonProps } from '~/app/types/ButtonProps'
import Paragraph from '../atoms/Paragraph'

/**
 *
 * @returns: Navigationen for hjemmesiden.
 * @example: <Navigation />
 * @alias: Navigation
 * @module: components/Navigation
 * @summary: Denne komponent bruges til at vise navigationen for hjemmesiden.
 * @see: src/components/Navigation.tsx
 * @version: 1.0.0
 * @property: [onClose, regular]
 * @author: Kasper Buchholtz
 *
 **/

export async function getNavigation() {
  return client.fetch(
    groq`*[_type == "navigation"]{
        _id,
        unique {
          ${ButtonQuery},
        },
        links[] {
          ...,
          ${ButtonQuery},
          subLinks[] {
            ${ButtonQuery},
          }
        },
      }`,
  )
}
interface LinkType {
  phone: any
  _ref: any
  email: any
  type: string
  label: string
  url: string
  blank?: boolean
  internalLink: {
    _type?: string
  }
}

interface SubLinkType {
  _key: string
  link: LinkType
}

interface NavLinkType {
  _id: string
  links: {
    _key: string
    link: LinkType
    subLinks?: ButtonProps[]
  }[]
}

interface NavigationProps {
  navigation: NavLinkType[]
  social: SocialType
  onClose: () => void
  toggleActiveLink: (key: string) => void
  activeLinkId: string | null
}

interface SocialType {
  platform: string
  url: string
}

export default function Navigation({
  onClose,
  regular,
  isFirstPageTitle,
}: {
  onClose?: () => void
  regular?: boolean
  isFirstPageTitle?: boolean
}) {
  const [navigation, setNavigation] = React.useState<NavLinkType[]>([])
  const [footer, setFooter] = React.useState<FooterType[]>([])

  // close nav on esc key
  useEffect(() => {
    getNavigation().then((nav) => setNavigation(nav))
    getFooter().then((nav) => setFooter(nav))
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  const [activeLinkId, setActiveLinkId] = React.useState('')
  const [isScrolled, setIsScrolled] = React.useState(false)
  const data = footer[0]
  const social = data?.social ?? []

  const toggleActiveLink = (linkId: string) => {
    setActiveLinkId((prevLinkId) => (prevLinkId === linkId ? '' : linkId))
  }

  const handleScroll = () => {
    if (typeof window !== 'undefined' && window.scrollY > 100) {
      setIsScrolled(true)
    } else {
      setIsScrolled(false)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll)
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])
  return (
    <>
      {regular ? (
        <nav
          key={'regular'}
          data-lenis-prevent="true"
          className="hidden my-auto lg:block lg:col-span-10 xl:col-span-20 -col-start-1"
        >
          <div className="">
            {navigation.map((nav) => (
              <div key={nav._id}>
                <ul className={`font-extralight flex gap-6 justify-end ${isFirstPageTitle ? 'text-white' : 'text-rødtekst'} ${isScrolled ? '!text-rødtekst' : ''}`}>
                  {nav?.links?.map((item) => (
                    <li
                      onMouseOverCapture={() => toggleActiveLink(item._key)}
                      onMouseOutCapture={() => toggleActiveLink(item._key)}
                      key={item._key}
                      className={`font-extralight ${isFirstPageTitle ? 'text-white' : 'text-rødtekst'} ${isScrolled ? '!text-rødtekst' : ''} relative ${item.subLinks && item.subLinks.length > 0 ? 'has-children' : 'has-no-children'}`}
                    >
                      {item.subLinks && item.subLinks.length > 0 ? (
                        <AdvancedButton
                          className={`flex items-center w-full gap-2 text-left `}
                          onClick={() => toggleActiveLink(item._key)}
                          variant={'none'}
                        >
                          <Paragraph>
                          {item.link.label}
                          </Paragraph>
                          <Icon
                          className={`transition-all size-3 fill-current ${activeLinkId === item._key ? 'rotate-180' : ''}`}
                          type="chevronDown"
                          />
                        </AdvancedButton>
                      ) : (
                        <Button link={item.link} style="none" className='px-0 py-0 hyphens-auto'>
                          <Paragraph className='hyphens-auto'>
                            {item.link.label}
                          </Paragraph>
                        </Button>
                      )}
                      <AnimatePresence>
                        {item.subLinks &&
                          item.subLinks.length > 0 &&
                          activeLinkId === item._key && (
                            <motion.ul
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{
                                duration: 0.3,
                                ease: [0.77, 0, 0.18, 1],
                              }}
                              className="absolute bottom-0 left-0 px-2 pt-3 pb-3 mt-2 space-y-2 translate-y-full rounded shadow-md bg-lys w-60"
                            >
                              {item.subLinks.map((subLink, index) => (
                                <motion.li
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{
                                    duration: 0.3,
                                    delay: 0.25,
                                    ease: [0.77, 0, 0.18, 1],
                                  }}
                                  className="hover:bg-[#F2EDD8] py-1 text-dark rounded transition-all duration-200 ease-in-out"
                                  onClick={onClose}
                                  key={index}
                                >
                                  <Button
                                    link={subLink.link}
                                    style="none"
                                    className="justify-start w-full font-extralight hyphens-auto"
                                  >
                                    {subLink.link.label}
                                  </Button>
                                </motion.li>
                              ))}
                            </motion.ul>
                          )}
                      </AnimatePresence>
                    </li>
                  ))}
                  <div className="flex gap-4">
                    <Login isScrolled={isScrolled} isFirstPageTitle={isFirstPageTitle} />
                    <Unique isScrolled={isScrolled} isFirstPageTitle={isFirstPageTitle} unique={navigation} />
                  </div>
                </ul>
              </div>
            ))}
          </div>
        </nav>
      ) : (
        <React.Fragment key={'burger'}>
          <motion.nav
            data-lenis-prevent="true"
            initial={{ x: '100%' }}
            transition={{ stiffness: 100, ease: [0.76, 0, 0.24, 1] }}
            animate={{ x: 0 }}
            exit={{ x: '100%', opacity: 0, }}
            className={`fixed z-[999] top-0 right-0 w-screen h-screen sm:w-[50vw] md:w-[50vw] lg:w-[33vw] overflow-auto bg-lys`}
          >
            <div className="h-full px-6 pt-20 pb-12 overflow-auto md:pb-6 md:px-24 lg:px-19 xl:px-16 sm:px-13 sm:pt-32 md:pt-28 lg:pt-28">
              {navigation.map((nav) => (
                <div
                  className="flex flex-col items-center justify-between h-full"
                  key={nav._id}
                >
                  <ul className="w-full space-y-5 font-medium text-dark text-medium">
                    {nav?.links?.map((item) => (
                      <li
                        key={item._key}
                        className={`${item.subLinks && item.subLinks.length > 0 ? 'has-children' : 'has-no-children'}`}
                      >
                        {item.subLinks && item.subLinks.length > 0 ? (
                          <AdvancedButton
                            className="flex items-center justify-between w-full px-0 text-left "
                            onClick={() => toggleActiveLink(item._key)}
                            variant={'none'}
                          >
                            <span className={`font-medium text-regular ${activeLinkId === item._key ? 'text-rød' : ''}`}>
                              {item.link.label}
                            </span>
                            <Icon
                              className={`transition-all size-3 fill-dark group-hover:fill-rød ${activeLinkId === item._key ? 'rotate-180' : ''}`}
                              type="chevronDown"
                            />
                          </AdvancedButton>
                        ) : (
                          <Button
                            link={item.link}
                            style="none"
                            className="block h-auto px-0 text-regular hyphens-auto hover:text-rød"
                          >
                            {item.link.label}
                          </Button>
                        )}
                        <AnimatePresence>
                          {item.subLinks &&
                            item.subLinks.length > 0 &&
                            activeLinkId === item._key && (
                              <motion.ul
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="mt-2 space-y-2"
                              >
                                {item.subLinks.map((subLink, index) => (
                                  <li
                                    className=""
                                    onClick={onClose}
                                    key={index}
                                  >
                                    <Button
                                      link={subLink.link}
                                      style="none"
                                      className="h-auto px-0 text-regular"
                                    >
                                      {subLink.link.label}
                                    </Button>
                                  </li>
                                ))}
                              </motion.ul>
                            )}
                        </AnimatePresence>
                      </li>
                    ))}
                  </ul>
                  <div className=" mb-24 mt-auto *:*:!w-full *:!w-full w-full flex align-center items-end justify-end h-full">
                    <Unique unique={navigation} />
                  </div>
                </div>
              ))}
            </div>
          </motion.nav>
          <motion.button
            className="fixed z-[998] top-0 right-0 w-screen h-screen bg-dark/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, stiffness: 100, ease: [0.76, 0, 0.24, 1] }}
            onClick={onClose}
          />
        </React.Fragment>
      )}
    </>
  )
}


const Unique = ({ unique, isFirstPageTitle, isScrolled }: { unique: any, isFirstPageTitle?: boolean, isScrolled?: any }) => {
  return (
    <>
      {unique.map((nav: any) => (
        <React.Fragment key={nav.unique?.link?.label}>
          <div className="">
            <Button
              link={nav?.unique?.link}
              style={isScrolled ? 'default' : isFirstPageTitle ? 'tertiary' : 'default'}
            >
              {nav?.unique?.link?.label}
            </Button>
          </div>
        </React.Fragment>
      ))}
    </>
  )
}

export const Login = ({ isFirstPageTitle, isScrolled }: { isFirstPageTitle?: boolean, isScrolled?: any }) => {
  return (
     <div className="relative flex flex-col items-center justify-center group">
      <Link href="https://mindkvplan.dk/users/login" target="_blank">
        {/*<Icon type="user" className={`${isFirstPageTitle ? 'md:fill-white' : 'fill-white md:fill-DabraRød'} ${isScrolled ? '!fill-DabraRød' : ''} size-7 my-auto`} />
      */}</Link>
      <span className={`${isFirstPageTitle ? 'bg-white text-DabraRød' : 'bg-DabraRød text-white'} ${isScrolled ? 'text-DabraRød' : ''} absolute invisible px-3 py-1 transition-all duration-200 ease-in-out rounded opacity-0 whitespace-nowrap -translate-y-1/4 right-full -bottom-full group-hover:opacity-100 group-hover:visible`}>
        Log ind
      </span>
    </div> 
  )
}
